/**
 * A list row's Active/Inactive status, switchable in its own cell (2026-09-29, the project owner's
 * request). The row's own status is the control, as in
 * vendor/isomorphic/apps/isomorphic/src/app/shared/appointment/appointment-list/list/columns.tsx,
 * drawn as the `Switch` of
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/shipping-info.tsx
 * — the same switch the edit form's `StatusSwitch` uses. Without `onChange` (no write permission)
 * it is the read-only badge the column always showed.
 *
 * The switch waits for the server and re-reads the list rather than flipping locally; a refusal
 * (e.g. the Default Tier) comes back as the API's own words in a toast. A click on it is its own —
 * `ClickableRow` skips inputs and labels, so it never opens the row.
 */
import { useState } from "react";
import { useRevalidator } from "react-router-dom";
import toast from "react-hot-toast";
import { Switch } from "rizzui";
import { Badge as DomainBadge } from "@steelyard/theme";
import { problemMessage } from "../dashboard/tone";

/** The switch's words for a setting that is not a record's status, e.g. `{ on: "Enabled", off: "Disabled" }`. */
export type ToggleLabels = { on: string; off: string };

export function StatusToggle({
  name,
  active,
  onChange,
  labels,
}: {
  name: string;
  active: boolean;
  /** Sets the record's status server-side. Absent → read-only badge. */
  onChange?: (next: boolean) => Promise<unknown>;
  /** Absent → Active/Inactive. */
  labels?: ToggleLabels;
}) {
  if (!onChange) return <DomainBadge kind={active ? "active" : "inactive"} />;
  return <LiveToggle name={name} active={active} onChange={onChange} labels={labels} />;
}

function LiveToggle({
  name,
  active,
  onChange,
  labels,
}: {
  name: string;
  active: boolean;
  onChange: (next: boolean) => Promise<unknown>;
  labels?: ToggleLabels;
}) {
  const now = active ? labels?.on : labels?.off;
  const next = active ? labels?.off : labels?.on;
  const revalidator = useRevalidator();
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      await onChange(!active);
      toast.success(`${name}: ${next ? next.toLowerCase() : active ? "deactivated" : "activated"}`);
      revalidator.revalidate();
    } catch (err) {
      toast.error(problemMessage(err, `${name}: Infra Junction refused that change`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Switch
      size="sm"
      checked={active}
      disabled={busy || revalidator.state === "loading"}
      onChange={() => void toggle()}
      aria-label={
        now && next
          ? `${name} is ${now.toLowerCase()} — switch to ${next.toLowerCase()}`
          : `${name} is ${active ? "active" : "inactive"} — switch to ${active ? "deactivate" : "activate"}`
      }
      label={now ?? (active ? "Active" : "Inactive")}
      labelClassName="text-sm text-gray-700"
    />
  );
}
